import { useState } from 'react';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Bed, MapPin, Star, Instagram, Wifi, CheckCircle2, Sparkles, ImageIcon, ExternalLink, ChevronDown } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { DestinationStay } from '@/hooks/useDestinationStays';
import { StayPhotoLightbox } from './StayPhotoLightbox';
import { trackEvent, inferAffiliatePartner, currentTrafficSource } from '@/lib/analytics';

const formatPrice = (s: DestinationStay) => {
  if (s.priceFrom == null) return null;
  const symbol = s.currencyCode === 'USD' ? '$' : s.currencyCode === 'EUR' ? '€' : s.currencyCode === 'GBP' ? '£' : '';
  return `${symbol}${s.priceFrom}${symbol ? '' : ` ${s.currencyCode ?? ''}`}`;
};

export const StayCard = ({ stay, destinationName }: { stay: DestinationStay; destinationName?: string }) => {
  const [expanded, setExpanded] = useState(false);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [photoIndex, setPhotoIndex] = useState(0);
  const price = formatPrice(stay);
  const bookingHref = stay.affiliateUrl || stay.websiteUrl || stay.instagramUrl || '#';

  const handleToggleExpand = () => {
    setExpanded((v) => {
      const next = !v;
      if (next) {
        trackEvent('accommodation_viewed', {
          accommodation_name: stay.name,
          destination_name: destinationName,
          current_page: typeof window !== 'undefined' ? window.location.pathname : undefined,
        });
      }
      return next;
    });
  };

  const handleAffiliateClick = () => {
    if (!stay.affiliateUrl) return;
    trackEvent('affiliate_link_clicked', {
      destination_name: destinationName,
      accommodation_name: stay.name,
      affiliate_partner: inferAffiliatePartner(stay.affiliateUrl),
      current_page: typeof window !== 'undefined' ? window.location.pathname : undefined,
      traffic_source: currentTrafficSource(),
    });
  };

  const allPhotos = stay.heroImageUrl
    ? [{ url: stay.heroImageUrl, alt: stay.name }, ...stay.galleryImages]
    : stay.galleryImages;

  const openPhotoAt = (index: number) => {
    setPhotoIndex(index);
    setLightboxOpen(true);
  };

  return (
    <Card className="bg-card border-border/50 overflow-hidden">
      <div className="grid md:grid-cols-[420px_1fr] lg:grid-cols-[480px_1fr]">
        {/* Image column */}
        <div className="flex flex-col">
          <div
            className="relative aspect-[16/10] md:aspect-[4/3] bg-secondary/30 flex items-center justify-center cursor-pointer"
            onClick={() => openPhotoAt(0)}
          >
            {stay.heroImageUrl ? (
              <img src={stay.heroImageUrl} alt={stay.name} className="w-full h-full object-cover" />
            ) : (
              <ImageIcon className="h-10 w-10 text-muted-foreground/50" />
            )}
            <div className="absolute top-3 left-3 flex gap-2">
              {stay.category && (
                <Badge className="bg-primary text-primary-foreground border-0 uppercase text-[10px] tracking-wider">
                  {stay.category}
                </Badge>
              )}
              {stay.isSwellBreaksPick && (
                <Badge className="bg-chart-4 text-background border-0 uppercase text-[10px] tracking-wider gap-1">
                  <Sparkles className="h-3 w-3" /> Pick
                </Badge>
              )}
            </div>
          </div>
          {!expanded && stay.galleryImages.length > 0 && (
            <div className="grid grid-cols-3 gap-1 p-1 bg-secondary/20">
              {stay.galleryImages.slice(0, 3).map((img, idx) => (
                <button
                  key={idx}
                  type="button"
                  onClick={() => openPhotoAt(idx + 1)}
                  className="block aspect-square overflow-hidden bg-secondary/30 text-left cursor-pointer"
                >
                  <img
                    src={img.url}
                    alt={img.alt ?? `${stay.name} photo ${idx + 2}`}
                    loading="lazy"
                    className="w-full h-full object-cover hover:scale-105 transition-transform"
                  />
                </button>
              ))}
            </div>
          )}
          {expanded && stay.galleryImages.length > 0 && (
            <div className="grid grid-cols-2 gap-1 p-1 bg-secondary/20">
              {stay.galleryImages.map((img, idx) => (
                <button
                  key={idx}
                  type="button"
                  onClick={() => openPhotoAt(idx + 1)}
                  className="block aspect-square overflow-hidden bg-secondary/30 text-left cursor-pointer"
                >
                  <img
                    src={img.url}
                    alt={img.alt ?? `${stay.name} photo ${idx + 2}`}
                    loading="lazy"
                    className="w-full h-full object-cover hover:scale-105 transition-transform"
                  />
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Content */}
        <div className="p-4 md:p-5 flex flex-col gap-3">
          <div className="flex items-start justify-between gap-3">
            <div className="space-y-1">
              <h3 className="font-serif text-xl md:text-2xl text-foreground">{stay.name}</h3>
              {stay.shortLocation && (
                <div className="flex items-center gap-1.5 text-sm text-muted-foreground">
                  <MapPin className="h-3.5 w-3.5 text-primary" />
                  {stay.shortLocation}
                </div>
              )}
              {stay.reviewScore != null && (
                <div className="flex items-center gap-1.5 text-sm">
                  <Star className="h-3.5 w-3.5 fill-primary text-primary" />
                  <span className="font-medium text-foreground">{stay.reviewScore}</span>
                  {stay.reviewCount != null && (
                    <span className="text-muted-foreground">({stay.reviewCount} reviews)</span>
                  )}
                </div>
              )}
            </div>
            {stay.instagramUrl && (
              <a
                href={stay.instagramUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="p-2 rounded-full bg-secondary/50 hover:bg-secondary text-primary transition-colors shrink-0"
                aria-label="Instagram"
              >
                <Instagram className="h-4 w-4" />
              </a>
            )}
          </div>

          {/* Facilities chips */}
          {stay.facilities.length > 0 && (
            <div className="flex flex-wrap gap-1.5">
              {stay.facilities.slice(0, expanded ? stay.facilities.length : 7).map((f) => (
                <Badge key={f} variant="secondary" className="font-normal text-xs">
                  {f}
                </Badge>
              ))}
              {!expanded && stay.facilities.length > 7 && (
                <Badge variant="outline" className="font-normal text-xs">
                  +{stay.facilities.length - 7}
                </Badge>
              )}
            </div>
          )}

          {stay.description && (
            <p className={cn('text-sm text-muted-foreground leading-relaxed', !expanded && 'line-clamp-3')}>
              {stay.description}
            </p>
          )}

          {/* Expanded details */}
          {expanded && (
            <div className="space-y-4 pt-2 border-t border-border/40">
              {stay.whyWePickedIt && (
                <div className="space-y-1.5">
                  <div className="flex items-center gap-1.5 text-primary font-medium text-sm uppercase tracking-wide">
                    <CheckCircle2 className="h-4 w-4" /> Why we picked it
                  </div>
                  <p className="text-sm text-muted-foreground leading-relaxed">{stay.whyWePickedIt}</p>
                </div>
              )}

              {stay.inclusions.length > 0 && (
                <div className="space-y-1.5">
                  <div className="flex items-center gap-1.5 text-primary font-medium text-sm uppercase tracking-wide">
                    <Wifi className="h-4 w-4" /> What's included
                  </div>
                  <div className="flex flex-wrap gap-1.5">
                    {stay.inclusions.map((i) => (
                      <Badge key={i} variant="outline" className="font-normal text-xs">{i}</Badge>
                    ))}
                  </div>
                </div>
              )}

              {stay.bestFor.length > 0 && (
                <div className="space-y-1.5">
                  <div className="flex items-center gap-1.5 text-primary font-medium text-sm uppercase tracking-wide">
                    <Bed className="h-4 w-4" /> Best for
                  </div>
                  <div className="flex flex-wrap gap-1.5">
                    {stay.bestFor.map((b) => (
                      <Badge key={b} variant="outline" className="font-normal text-xs">{b}</Badge>
                    ))}
                  </div>
                </div>
              )}

              {stay.tags.length > 0 && (
                <div className="flex flex-wrap gap-1.5">
                  {stay.tags.map((t) => (
                    <Badge key={t} variant="secondary" className="font-normal text-xs">{t}</Badge>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Footer */}
          <div className="flex items-center justify-between gap-3 pt-3 mt-auto border-t border-border/40">
            <div>
              {price && (
                <>
                  <div className="text-2xl font-serif font-semibold text-foreground">{price}</div>
                  <div className="text-xs text-muted-foreground">per night</div>
                </>
              )}
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="ghost"
                size="sm"
                onClick={handleToggleExpand}
                className="text-xs"
              >
                {expanded ? 'Less' : 'More'}
                <ChevronDown className={cn('ml-1 h-3.5 w-3.5 transition-transform', expanded && 'rotate-180')} />
              </Button>
              {bookingHref !== '#' && (
                <Button asChild size="sm" className="rounded-full">
                  <a href={bookingHref} target="_blank" rel="noopener noreferrer" onClick={handleAffiliateClick}>
                    {stay.bookingButtonText}
                    <ExternalLink className="ml-1.5 h-3.5 w-3.5" />
                  </a>
                </Button>
              )}
            </div>
          </div>
          {stay.affiliateUrl && (
            <p className="text-[10px] text-muted-foreground/70 italic">
              Affiliate link — we may earn a commission at no extra cost to you.
            </p>
          )}
        </div>
      </div>
      <StayPhotoLightbox
        open={lightboxOpen}
        onOpenChange={setLightboxOpen}
        photos={allPhotos}
        currentIndex={photoIndex}
        onSelectIndex={setPhotoIndex}
        stayName={stay.name}
      />
    </Card>
  );
};
